import { apiPaths } from '@/constants/apiPaths';
import { ConversionMeta } from '@/integrations/tracking-service/conversions/conversion-meta';
import { DataLayerHelper } from './data-layer-helper';
import { slugify } from './url-helper';

/**
 * Submit a conversion event to the internal tracking endpoint (Metabase).
 * Failures are swallowed so a tracking outage cannot break the user-visible flow.
 */
async function sendTrackingServiceConversion(
  conversion: string,
  payload: Record<string, unknown> = {},
  meta?: ConversionMeta,
): Promise<void> {
  try {
    await fetch(apiPaths.metadata.trackConversions, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ conversion, ...payload, ...(meta ? { meta } : {}) }),
    });
  } catch {
    // Tracking failures are non-critical — the user-visible flow continues regardless.
  }
}

/**
 * Fire a conversion event to the internal tracking service AND the GTM data layer.
 * `meta` only goes to the tracking service, where it is stored as conversions metadata rows.
 */
export async function trackConversion(
  conversion: string,
  payload: Record<string, unknown> = {},
  meta?: ConversionMeta,
): Promise<void> {
  DataLayerHelper.pushConversionEvent(conversion, payload);
  await sendTrackingServiceConversion(conversion, payload, meta);
}

/**
 * Builds a tracking id from a prefix and one or more text parts.
 *
 * - Each text part is slugified and joined with the prefix using `-`.
 * - Empty / undefined parts are dropped.
 * - Returns `undefined` when the prefix is missing or all parts slug to empty.
 *
 * @example
 * buildTrackingId('tile-1', 'Buy Now')                   // 'tile-1-buy-now'
 * buildTrackingId('tile-1', 'Smart Lock Pro', 'image')   // 'tile-1-smart-lock-pro-image'
 * buildTrackingId(undefined, 'Buy Now')                  // undefined
 * buildTrackingId('tile-1', '')                          // undefined
 */
export function buildTrackingId(
  prefix: string | undefined,
  ...parts: (string | null | undefined)[]
): string | undefined {
  if (!prefix) {
    return undefined;
  }
  const slugged = parts
    .map((part) => (part ? slugify(part) : ''))
    .filter((part) => part.length > 0);
  if (slugged.length === 0) {
    return undefined;
  }
  return [prefix, ...slugged].join('-');
}
